import { TTextBlock } from "@page-builder/ts/generated";
import React, { useEffect, useState, useRef } from "react";
import styles from "./styles.module.scss";
import {
    formatPhoneNumber,
    getPhoneNumber,
    getTitle,
    replacePlaceholders,
    stripHtmlTags,
} from "@lib/sharedUtils";
import { ListingItem } from "@lib/shared/types";
import { getAffiliateListId } from "@lib/shared/cookies";
import { useDomainContext } from "@hooks/useDomainContext";
import CloseIcon from "@components/svg/closeIcon";
import dynamic from "next/dynamic";
import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import { useRouter } from "next/router";
import { stackAction } from "@services/actionsTracker";
import { getCallsCampaignTitleBasedOnCategory } from "./utils";
const Modal = dynamic(() => import("@components/shared/modal"));

export default function TextBlock({
    props = {},
}: {
    props: TTextBlock["props"];
}) {
    const { query } = useRouter();
    const [showModal, setShowPartnerPopup] = useState(false);
    const {
        text,
        modalContent,
        modalTriggerId,
        modalTitle,
        scrollToTopTriggerId,
    } = props;
    const {
        category,
        visitDetails,
        routeConfig,
        formattedTitle,
        domain,
        form,
        headerOffer,
    } = useDomainContext();
    const genericRoute = routeConfig?.replacedPortions?.find(
        (portion) => portion.key === "[generic-route]",
    )?.value;
    const keyword = routeConfig?.replacedPortions?.find(
        (portion) => portion.key === "[keyword]",
    )?.value;

    const phoneNumber = formatPhoneNumber(getPhoneNumber(headerOffer?.[0]));
    const textBlockRef = useRef<HTMLDivElement>(null);

    const offers = domain?.defaultOffers;
    const affiliateListId = getAffiliateListId();

    // Replace [offer:<slug>] placeholders with an internal offer route.
    // This keeps link resolution consistent with existing /offer redirect flow (getOfferUrl)
    const replaceOfferLinkPlaceholders = (
        input: string,
        offersList: ListingItem[] | undefined,
    ): string => {
        if (!input || !offersList?.length) return input;

        const getOfferTargetUrl = (offer: ListingItem): string => {
            const categoryId = offer?.category?.id;
            const affLinkId =
                typeof query?.affLinkId === "string" ? query.affLinkId : "";
            const params = new URLSearchParams();

            if (affiliateListId) {
                params.append("id", String(affiliateListId));
            }

            if (categoryId) {
                params.append("categoryId", String(categoryId));
            }

            params.append("zone", "external");

            if (affLinkId) {
                params.append("affLinkId", affLinkId);
            }

            return `/offer/${offer.slug}?${params.toString()}`;
        };

        return input.replace(/\[offer:([^\]]+)\]/gi, (placeholder, slugRaw) => {
            const slug = String(slugRaw).trim().toLowerCase();
            const matchedOffer = offersList.find(
                (offer) => offer.slug?.toLowerCase() === slug,
            );

            if (!matchedOffer) {
                return placeholder;
            }

            return getOfferTargetUrl(matchedOffer);
        });
    };

    useEffect(() => {
        if (!modalTriggerId || !modalContent) return;
        const el = document.getElementById(modalTriggerId);
        if (!el) return;

        const onClick = () => setShowPartnerPopup(true);
        el.addEventListener("click", onClick);
        el.classList.add(styles["modal-trigger"]);

        return () => {
            el.removeEventListener("click", onClick);
            el.classList.remove(styles["modal-trigger"]);
        };
    }, [modalTriggerId, modalContent]);

    useEffect(() => {
        if (scrollToTopTriggerId) {
            const scrollTrigger = document.getElementById(scrollToTopTriggerId);
            const handleScrollToTop = () => {
                window.scroll({ top: 0, left: 0, behavior: "smooth" });
            };
            scrollTrigger?.addEventListener("click", handleScrollToTop);

            return () => {
                scrollTrigger?.removeEventListener("click", handleScrollToTop);
            };
        }
    }, [scrollToTopTriggerId]);

    // Add event listeners to all links in the text block
    useEffect(() => {
        if (textBlockRef.current) {
            const links = textBlockRef.current.querySelectorAll("a");

            const handleLinkClick = (event: MouseEvent) => {
                const target = event.currentTarget as HTMLAnchorElement;
                const href = target.getAttribute("href") || "";
                const sourceLink =
                    new URLSearchParams(href).get("sourceLink") || "";
                if (sourceLink) {
                    stackAction({
                        action: "click",
                        category: "link",
                        label: "sourceLink",
                        value: sourceLink,
                        createdAt: new Date().toISOString(),
                    });
                }
            };

            links.forEach((link) => {
                link.addEventListener("click", handleLinkClick);
            });

            // Cleanup function to remove event listeners
            return () => {
                links.forEach((link) => {
                    link.removeEventListener("click", handleLinkClick);
                });
            };
        }
    }, [text]); // Re-run when text content changes

    const formTitle = getTitle({
        category,
        visitDetails,
        formattedTitle,
        textBlocks: domain?.textBlocks,
        stepTitle: form?.steps?.[0]?.title,
    });

    const textBlockDescription =
        domain?.textBlocks &&
        domain?.textBlocks.length &&
        (domain?.textBlocks.find((item) => item.position === "form_description")
            ?.text as string);

    const formDescription =
        textBlockDescription || form?.steps?.[0]?.description;

    const queryString = gettingQueriesAsString(query, "inline", ["slug"]);

    const callsCampaignTitle =
        domain?.textBlocks?.find(
            (block) => block.position === "calls-campaign-title",
        )?.text ||
        formattedTitle ||
        getCallsCampaignTitleBasedOnCategory(category?.slug);

    const titleWithFallback =
        formattedTitle ??
        category?.config?.nativeNetworksPageTitleFallback ??
        "";

    const textPlaceholderValues = {
        category: category?.name,
        region: visitDetails.region,
        city: visitDetails?.city,
        keyword,
        genericRoute,
        title: titleWithFallback,
        queryString,
        callsCampaignTitle,
    };

    const textWithPlaceholders = text
        ? replacePlaceholders(text, textPlaceholderValues)
        : "";

    const textWithFormTokens = textWithPlaceholders
        .replace(/\[form-title\]/gi, stripHtmlTags(formTitle || ""))
        .replace(
            /\[form-description\]/gi,
            stripHtmlTags(formDescription || ""),
        );

    // Replace [offer:<slug>] placeholders with the matching offer URL
    const textWithOfferLinks = replaceOfferLinkPlaceholders(
        textWithFormTokens,
        offers,
    );

    // this removes any div that has class "phone-number-hide" and its content if there's [phoneNumber] placeholder in the text and phone number is not available, otherwise replace the [phoneNumber] placeholder with the formatted phone number
    const renderedHtml = phoneNumber
        ? textWithOfferLinks.replace(/\[phoneNumber\]/gi, phoneNumber)
        : textWithOfferLinks.replace(
              /<div>\s*<a[^>]*class="phone-number-hide"[^>]*>[\s\S]*?<\/a>\s*<\/div>/gi,
              "",
          );

    return (
        <>
            <div
                ref={textBlockRef}
                dangerouslySetInnerHTML={{
                    __html: renderedHtml,
                }}
                className={`${styles["text-block"]} prose md:prose-md max-w-none`}
            />
            {modalTriggerId && modalContent && (
                <Modal
                    onClose={() => {
                        setShowPartnerPopup(false);
                    }}
                    active={showModal && !!modalContent}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalStyles={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "100%",
                    }}
                    modalClassName="h-[90vh] rounded"
                    bodyStyles={{ overflowY: "scroll" }}
                    width={900}
                    header={modalTitle}
                >
                    <div
                        className="pb-5 lg:px-9 px-5 modalContainer"
                        dangerouslySetInnerHTML={{
                            __html: modalContent,
                        }}
                    />
                </Modal>
            )}
        </>
    );
}
